import React, { useCallback, useEffect, useMemo, useState } from "react"
import styled, { keyframes } from "styled-components"
import { useCookies } from "react-cookie"
import Button from "./elements/button"
import media from "../styles/media"

const easeIn = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`

const StyledContainer = styled.div`
  display: flex;
  position: fixed;
  bottom: 1rem;
  left: 0;
  right: 0;
  align-items: baseline;
  justify-content: center;
  padding: 0 1rem;
  z-index: 500;
  opacity: 0;
  animation: ${easeIn} 1s ease 1s forwards;
  ${media.sm`
    padding: 0;
    bottom: 0;
  `}
`

const StyledButton = styled(Button)`
  font-size: 1rem;
  background-color: ${({ theme }) => theme.color.blue600};

  ${media.sm`
    flex: 1;
  `}
  
  &:hover {
    background-color: ${({ theme }) => theme.color.blue500};
  }
`

const StyledBanner = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  justify-content: stretch;
  flex: 1;
  background: ${({ theme }) => theme.color.blue800};
  color: white;
  padding: 1rem 1.25rem 1rem 2rem;
  max-width: 1280px;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  border-radius: 3rem;
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.1);

  ${media.sm`
    border-radius: 1rem 1rem 0 0;
    padding: 1rem 1.25rem;
    grid-template-columns: 1fr;
  `}
`

const StyledText = styled.p`
  margin: 0;
  flex: 1;
  flex-basis: 500px;
`

const StyledButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
`

const ACCEPT_CONSENT = {
  adConsentGranted: true,
  analyticsConsentGranted: true,
  functionalityConsentGranted: true,
  personalizationConsentGranted: true,
  securityConsentGranted: true,
  adUserDataConsentGranted: true,
  adPersonalizationConsentGranted: true,
}

const REJECT_CONSENT = {
  adConsentGranted: false,
  analyticsConsentGranted: false,
  functionalityConsentGranted: false,
  personalizationConsentGranted: false,
  securityConsentGranted: false,
  adUserDataConsentGranted: false,
  adPersonalizationConsentGranted: false,
}

const COOKIE_CONSENT = "klipfolio_consent"

const Consent = () => {
  const [cookies, setCookie] = useCookies([COOKIE_CONSENT])
  const consentSettings = useMemo(() => cookies[COOKIE_CONSENT], [cookies])
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  const accept = useCallback(() => {
    window.consentListeners?.forEach(callback => {
      callback(ACCEPT_CONSENT)
    })
    setCookie(COOKIE_CONSENT, ACCEPT_CONSENT)
  }, [setCookie])

  const reject = useCallback(() => {
    window.consentListeners?.forEach(callback => {
      callback(REJECT_CONSENT)
    })
    setCookie(COOKIE_CONSENT, REJECT_CONSENT)
  }, [setCookie])

  if (!isClient || consentSettings !== undefined) {
    return null
  }

  return (
    <StyledContainer>
      <StyledBanner>
        <StyledText>
          We use third-party analytics cookies to understand how you use our
          website so we can build better products.
        </StyledText>
        <StyledButtonContainer flexFlow="row" gap="1rem">
          <StyledButton onClick={accept} notice={false}>
            Accept
          </StyledButton>
          <StyledButton onClick={reject} notice={false}>
            Reject
          </StyledButton>
        </StyledButtonContainer>
      </StyledBanner>
    </StyledContainer>
  )
}

export default Consent
